import { getColumnCurrencyInfo, isColumnCurrencyFormatType, } from '@thoughtspot/formatter-service';
import { getId, isAttribute, isEffectivelyNonNumeric, isEffectivelyNumeric, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { generateQueryFromAxisConfig } from '../data/data-util';
import { ChartType } from '../types';
import { createLogger } from '../utils/logger';
import { areAllTimeSeriesColumns, getAllColumnsInAxisConfig, getChartType, isDescendingSort, parseQueryDefinitionFromVisualAxisConfig, } from './chart-model-utils';
import { areAllAttributes, areAllEffectivelyNumeric, configOptions, } from './chart-type-specification-service';
import { getName } from './column-name-util';
const logger = createLogger('axis-column-validation-service');
function getCurrencyColumnGuid(vizColumn) {
    if (!isColumnCurrencyFormatType(vizColumn))
        return undefined;
    const columnCurrencyInfo = getColumnCurrencyInfo(vizColumn);
    return columnCurrencyInfo.column;
}
const hasColumnInQuery = (axisConfig, chartViz, colId) => {
    const queries = parseQueryDefinitionFromVisualAxisConfig(getChartType(chartViz), axisConfig, chartViz).map((query, idx) => generateQueryFromAxisConfig(query, idx));
    return _.some(queries, query => {
        return query
            .getColumnsInDataOrder()
            .map(col => getId(col))
            .includes(colId);
    });
};
export function hasCurrencyColData(vizColumn, vizColumns, currencyColumnGuid, axisConfig, chartViz) {
    const currencyColumnIndex = vizColumns.findIndex(col => getId(col) === currencyColumnGuid);
    if (currencyColumnIndex === -1) {
        logger.error('Currency code column is missing in columns list');
        return false;
    }
    const hasCurrencyColumnInQuery = hasColumnInQuery(axisConfig, chartViz, currencyColumnGuid);
    if (!hasCurrencyColumnInQuery) {
        logger.warn('currency code column is missing in the query');
    }
    return hasCurrencyColumnInQuery;
}
function validateCurrencyInfo(columns, axisConfig, chartViz) {
    const validationPassed = columns.every(vizColumn => {
        const currencyColumnGuid = getCurrencyColumnGuid(vizColumn);
        if (currencyColumnGuid !== undefined &&
            !hasCurrencyColData(vizColumn, columns, currencyColumnGuid, axisConfig, chartViz)) {
            return false;
        }
        return true;
    });
    return validationPassed;
}
export function haveIdenticalCurrencyTypeInfo(vizColumns) {
    if (vizColumns.length === 0 || !vizColumns) {
        logger.error('No columns passed to haveIdenticalCurrencyTypeInfo');
        return false;
    }
    const currencyTypeInfo = getColumnCurrencyInfo(vizColumns[0]);
    return vizColumns
        .slice(1)
        .every(c => _.isEqual(currencyTypeInfo, getColumnCurrencyInfo(c)));
}
export function validateAxisColumns(originalAxisConfig, chartType, chartViz, hasSharedYAxisByDefault, isRadialEnabled, isYAxisDisabled = false, isXYAxisTransposed = false, isCategorySelectionEnabled = false, getTranslatedText) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const axisConfig = _.cloneDeep(originalAxisConfig);
    axisConfig.x = axisConfig.x || [];
    axisConfig.y = axisConfig.y || [];
    axisConfig.color = axisConfig.color || [];
    const chartConfigOptions = configOptions(chartType, null);
    const validity = {};
    const allColumns = getAllColumnsInAxisConfig(axisConfig);
    const columnIds = new Set([]);
    if (((_a = axisConfig.x) === null || _a === void 0 ? void 0 : _a.length) === 0 &&
        !chartConfigOptions.allowedConfigurations.optionalXAxisColumns) {
        validity.x = isXYAxisTransposed
            ? getTranslatedText('chartEditorErrorExplanation.ATLEAST_ONE_AXIS_COLUMN', {
                axis: getTranslatedText(chartConfigOptions.axesAlias.yAxis),
            })
            : getTranslatedText('chartEditorErrorExplanation.ATLEAST_ONE_AXIS_COLUMN', {
                axis: getTranslatedText(chartConfigOptions.axesAlias.xAxis),
            });
    }
    if (!isYAxisDisabled && ((_b = axisConfig.y) === null || _b === void 0 ? void 0 : _b.length) === 0) {
        validity.y = isXYAxisTransposed
            ? getTranslatedText('chartEditorErrorExplanation.ATLEAST_ONE_AXIS_COLUMN', {
                axis: getTranslatedText(chartConfigOptions.axesAlias.xAxis),
            })
            : getTranslatedText('chartEditorErrorExplanation.ATLEAST_ONE_AXIS_COLUMN', {
                axis: getTranslatedText(chartConfigOptions.axesAlias.yAxis),
            });
    }
    if (isRadialEnabled && !axisConfig.size) {
        validity.size = getTranslatedText('chartEditorErrorExplanation.SIZE_REQUIRED');
    }
    if (((_c = axisConfig.y) === null || _c === void 0 ? void 0 : _c.length) > 1) {
        if (!chartConfigOptions.allowedConfigurations
            .allowMultipleColumnsOnYAxis) {
            validity.y = getTranslatedText('chartEditorErrorExplanation.MULTIPLE_COLUMNS_ON_Y');
            return validity;
        }
        if (!chartConfigOptions.allowedConfigurations.allowAttributesInPivotData) {
            const invalid = axisConfig.y.some(column => {
                return !isEffectivelyNumeric(column);
            });
            if (invalid) {
                validity.y = getTranslatedText('chartEditorErrorExplanation.MULTIPLE_NON_NUMERIC_ON_Y');
            }
        }
    }
    if (hasSharedYAxisByDefault) {
        const areAllYAxisColumnsNumeric = (_d = axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.y) === null || _d === void 0 ? void 0 : _d.every(column => {
            return isEffectivelyNumeric(column);
        });
        if (!areAllYAxisColumnsNumeric) {
            validity.y = getTranslatedText('chartEditorErrorExplanation.NON_NUMERIC_Y_AXIS_LINKING');
        }
    }
    (_e = axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.color) === null || _e === void 0 ? void 0 : _e.forEach(column => {
        if (!isAttribute(column)) {
            validity.color = getTranslatedText('chartEditorErrorExplanation.ONLY_ATTRIBUTES_ON_LEGEND');
        }
    });
    if (axisConfig.size) {
        if (isEffectivelyNonNumeric(axisConfig.size)) {
            validity.size = getTranslatedText('chartEditorErrorExplanation.NUMERIC_SIZE_COLUMN');
        }
    }
    if (axisConfig.category && axisConfig.category.length) {
        if (!isCategorySelectionEnabled) {
            validity.category = getTranslatedText('chartEditorErrorExplanation.UNSUPPORTED_CATEGORY');
        }
        if (!isAttribute(axisConfig.category[0])) {
            validity.category = getTranslatedText('chartEditorErrorExplanation.ONLY_ATTRIBUTES_ON_CATEGORY');
        }
    }
    for (const column of allColumns) {
        if (column) {
            const columnId = getId(column);
            if (columnIds.has(columnId)) {
                validity.other = getTranslatedText('chartEditorErrorExplanation.COLUMN_ALREADY_USED', getName(column));
                break;
            }
            columnIds.add(columnId);
        }
    }
    if (chartType) {
        if (!chartConfigOptions.allowLegendWithMultipleY &&
            ((_f = axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.y) === null || _f === void 0 ? void 0 : _f.length) > 1 &&
            ((_g = axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.color) === null || _g === void 0 ? void 0 : _g.length) > 0) {
            if (chartType === ChartType.StackedBar ||
                chartType === ChartType.Bar) {
                validity.color = getTranslatedText('chartEditorErrorExplanation.UNSUPPORTED_LEGEND_WITH_MULTIPLE_X');
            }
            else {
                validity.color = getTranslatedText('chartEditorErrorExplanation.UNSUPPORTED_LEGEND_WITH_MULTIPLE_Y');
            }
        }
        if (!chartConfigOptions.noMeasuresOnXAxis) {
            if (!areAllAttributes(axisConfig.x)) {
                if (!areAllEffectivelyNumeric(axisConfig.x)) {
                    validity.x = getTranslatedText('chartEditorErrorExplanation.MEASURE_WITH_ATTRIBUTE_ON_X');
                }
                else if (((_h = axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.x) === null || _h === void 0 ? void 0 : _h.length) > 1) {
                    validity.x = getTranslatedText('chartEditorErrorExplanation.SINGLE_MEASURE_ALLOWED_ON_X');
                }
            }
        }
        if (chartConfigOptions.allowedConfigurations.onlyTimeSeriesOnXAxis) {
            if (!areAllTimeSeriesColumns(axisConfig.x)) {
                validity.x = getTranslatedText('chartEditorErrorExplanation.ONLY_TIME_ATTRIBUTES_ON_X');
            }
            else if (_.some(axisConfig.x, column => isDescendingSort(chartViz, column))) {
                validity.x = getTranslatedText('chartEditorErrorExplanation.ONLY_ASCENDING_TIME_ATTRIBUTES_ON_X');
            }
        }
        if (!validity.other &&
            !validateCurrencyInfo(allColumns, axisConfig, chartViz))
            validity.other = getTranslatedText('chartEditorErrorExplanation.NEEDS_CURRENCY_COLUMN');
    }
    if (hasSharedYAxisByDefault && ((_j = axisConfig === null || axisConfig === void 0 ? void 0 : axisConfig.y) === null || _j === void 0 ? void 0 : _j.length) > 1 && !validity.y) {
        if (!haveIdenticalCurrencyTypeInfo(axisConfig.y)) {
            validity.y = getTranslatedText('chartEditorErrorExplanation.INVALID_AXIS_SHARING');
        }
    }
    if (chartType === ChartType.Kpi &&
        axisConfig.y.length !== 0 &&
        axisConfig.x.length !== 0 &&
        areAllAttributes(axisConfig.y) &&
        areAllTimeSeriesColumns(axisConfig.x)) {
        validity.x = getTranslatedText('chartEditorErrorExplanation.KPI.XAXIS_TIMECOLUMN_FORBIDDEN_WITH_YAXIS_ATTRIBUTE');
    }
    return validity;
}
//# sourceMappingURL=axis-column-validation-service.js.map